/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual, sortBy } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { ChartOfAccountBulkFlatItem, Consolidation } from '@futrli-sdk/consolidations';
import { CreateConsolidationParams } from '@futrli/organisations/core/api/consolidation';
import { getWorkspaceConsolidationsById, Logger, markAsStale, usePromiseState } from '@futrli/shared-services';
import { toastError, toastDismiss, ToastId } from '@futrli/ui-components';
import { clearFinancialConfigQueryCache } from '@futrli/views/queries/financialConfig';

import {
  createConsolidation,
  fetchConsolidation,
  fetchMergedCOA,
  fetchSupportedConsolidationCurrencies,
  fetchConsolidationOrganisationInfo,
  fetchFXRates,
  updateConsolidation
} from '../../../core/api/consolidation/consolidation';
import { useWorkspaceOrganisations } from '../../../core/hooks/useWorkspaceOrganisations';
import {
  convertConsolidationDataToConfig,
  convertFxDataResponseToCurencyRateData,
  getStartEndGridDates,
  mergeOrganisationData
} from '../utils';
import { DailyFxDataValues, ExtendedOrganisation } from '../types';
import { ExtendedCurrency, FxConfig } from './types';
import { getExtendedAndSortedCurrencies } from './utils';

export const useConsolidationCRUD = (consolidationId?: string, selectedOrganisations?: ExtendedOrganisation[]) => {
  const [coaReq, setCoaReq] = usePromiseState<ChartOfAccountBulkFlatItem[]>();
  const [configReq, setConfigReq, configData] = usePromiseState<Consolidation>();
  const [currenciesReq, setCurrenciesReq] = usePromiseState<ExtendedCurrency[]>();
  const [fxRatesReq, setFxRatesReq] = usePromiseState<DailyFxDataValues>();
  const [organisationsReq, setOrganisationsReq] = usePromiseState<ExtendedOrganisation[]>();

  const [fxConfigBaseCurrency, setFxConfigBaseCurrency] = useState<FxConfig['presentationCurrency']>();
  const [fxConfigCurrencies, setFxConfigCurrencies] = useState<FxConfig['currencies']>([]);
  const [fxConfigYearEnd, setFxConfigYearEnd] = useState<FxConfig['yearEnd']>();
  const [isReady, setIsReady] = useState(false);

  // If config response changes, convert it to our config format
  const config = useMemo(() => {
    if (configData) {
      return convertConsolidationDataToConfig(configData);
    }
  }, [configData]);

  const { success: coaSuccess, failure: coaFailure } = coaReq;
  const { success: configSuccess, failure: configFailure } = configReq;
  const { success: currenciesSuccess, failure: currenciesFailure } = currenciesReq;
  const { success: organisationsSuccess, failure: organisationsFailure } = organisationsReq;

  const coaReady =
    !consolidationId || coaSuccess || coaFailure || configFailure || (config && config.organisations.length <= 1);
  const configReady = !consolidationId || configSuccess || configFailure;
  const currenciesReady = currenciesSuccess || currenciesFailure;
  const organisationsReady = organisationsSuccess || organisationsFailure;

  const dispatch = useDispatch();
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const { organisations: workspaceOrgs, isDataLoading: workspaceOrgsLoading } = useWorkspaceOrganisations(workspaceId);

  const toasts = useRef<Record<string, ToastId | undefined>>({
    coa: undefined,
    currencies: undefined,
    config: undefined,
    fxRates: undefined,
    organisationsInfo: undefined
  });

  const saveConsolidation = useCallback(
    async (data: CreateConsolidationParams) => {
      const response = config?.id
        ? await setConfigReq(updateConsolidation(workspaceId, config.id, data))
        : await setConfigReq(createConsolidation(workspaceId, data));

      await clearFinancialConfigQueryCache({
        workspaceId, entityType: 'consolidation', entityId: response.id
      }).catch(error => Logger.error(String(error)));
      dispatch(markAsStale('Consolidation'));
      dispatch(getWorkspaceConsolidationsById(workspaceId));

      return convertConsolidationDataToConfig(response);
    },
    [config?.id, dispatch, setConfigReq, workspaceId]
  );

  const updateFxConfig = useCallback(
    (newFxConfig: FxConfig) => {
      setFxConfigBaseCurrency(newFxConfig.presentationCurrency);
      if (!isEqual(newFxConfig.currencies, fxConfigCurrencies)) {
        setFxConfigCurrencies(newFxConfig.currencies);
      }
      if (!isEqual(newFxConfig.yearEnd, fxConfigYearEnd)) {
        setFxConfigYearEnd(newFxConfig.yearEnd);
      }
    },
    [fxConfigCurrencies, fxConfigYearEnd]
  );

  // ***********
  // * Effects *
  // ***********

  // Load available currencies list
  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchSupportedConsolidationCurrencies();
      return sortBy(data, c => c.currency);
    };

    const loadData = async () => {
      try {
        await setCurrenciesReq(fetchData().then(currencies => getExtendedAndSortedCurrencies(currencies)));
      } catch (e) {
        toasts.current.currencies = toastError(
          <FormattedMessage
            id="consolidation.failed_to_load_supported_currencies"
            defaultMessage="Failed to load the supported currencies list"
          />
        );
      }
    };

    loadData();
  }, [setCurrenciesReq]);

  // If all data has loaded for the first time, flag it
  useEffect(() => {
    if (configReady && coaReady && currenciesReady && organisationsReady) {
      setIsReady(true);
    }
  }, [coaReady, configReady, currenciesReady, organisationsReady]);

  // If IDs change, load config
  useEffect(() => {
    if (!workspaceOrgsLoading) {
      const fetchData = async (consolidationId: string) => {
        return await fetchConsolidation(workspaceId, consolidationId);
      };

      const loadData = async () => {
        try {
          if (consolidationId) {
            await setConfigReq(fetchData(consolidationId));
          }
        } catch (e) {
          toasts.current.config = toastError(
            <FormattedMessage
              id="consolidation.failed_to_load_config"
              defaultMessage="Failed to load consolidation configuration"
            />
          );
        }
      };
      loadData();
    }
  }, [consolidationId, setConfigReq, workspaceId, workspaceOrgsLoading]);

  // If workspace organisations change, load extra data and merge
  useEffect(() => {
    if (!workspaceOrgsLoading) {
      const fetchData = async () => {
        const extraOrgsData = await fetchConsolidationOrganisationInfo(workspaceId);
        return mergeOrganisationData(workspaceOrgs, extraOrgsData);
      };

      const loadData = async () => {
        try {
          await setOrganisationsReq(fetchData());
        } catch (e) {
          toasts.current.organisationsInfo = toastError(
            <FormattedMessage
              id="consolidation.failed_to_load_organisations"
              defaultMessage="Failed to load organisation data"
            />
          );
        }
      };

      loadData();
    }
  }, [setOrganisationsReq, workspaceId, workspaceOrgs, workspaceOrgsLoading]);

  // Load exchange rate data if multi-currency and selected org currencies list changes
  useEffect(() => {
    if (fxConfigBaseCurrency && fxConfigYearEnd && fxConfigCurrencies.length > 0) {
      const fetchData = async () => {
        const { startDate, endDate } = getStartEndGridDates(fxConfigYearEnd);
        const data = await fetchFXRates(fxConfigBaseCurrency, fxConfigCurrencies, startDate, endDate);
        return convertFxDataResponseToCurencyRateData(data);
      };

      const loadData = async () => {
        try {
          await setFxRatesReq(fetchData());
        } catch (e) {
          toasts.current.fxRates = toastError(
            <FormattedMessage id="consolidation.failed_to_load_fx_rates" defaultMessage="Failed to load FX rates" />
          );
        }
      };

      loadData();
    }
  }, [fxConfigBaseCurrency, fxConfigCurrencies, fxConfigYearEnd, setFxRatesReq]);

  // Load COA data if selected accounts change
  useEffect(() => {
    if (selectedOrganisations) {
      const fetchData = async () => {
        return await fetchMergedCOA(
          workspaceId,
          selectedOrganisations?.map(org => org.id)
        );
      };

      const loadData = async () => {
        try {
          if (selectedOrganisations.length > 1) {
            await setCoaReq(fetchData());
          }
        } catch (e) {
          toasts.current.config = toastError(
            <FormattedMessage id="consolidation.failed_to_load_coa" defaultMessage="Failed to load chart of accounts" />
          );
        }
      };

      loadData();
    }
  }, [selectedOrganisations, setCoaReq, workspaceId]);

  // Close toast on unmount if shown
  useEffect(
    () => () => {
      Object.values(toasts.current).forEach(toastId => {
        if (toastId) {
          toastDismiss(toastId);
        }
      });
    },
    []
  );

  return {
    coaReq,
    config,
    configReq,
    consolidationId,
    currenciesReq,
    fxRatesReq,
    isReady,
    organisationsReq,
    saveConsolidation,
    setFxConfig: updateFxConfig
  };
};
